import type { Channel } from './channel'
import type { PhoenixPushEventMap, Status, StringKeyof } from './types'

/** Push class */
export class Push<
  PushEventMap extends PhoenixPushEventMap = PhoenixPushEventMap,
  E extends StringKeyof<PushEventMap> = StringKeyof<PushEventMap>,
> {
  /** The received payload. */
  private receivedPayload:
    | {
        response:
          PushEventMap[E]['error_payload'] | PushEventMap[E]['success_payload']
        status: Status
      }
    | undefined

  /** The push timeout timer. */
  private timeoutTimer: ReturnType<typeof setTimeout> | undefined

  /** The list of reply event callbacks. */
  private recHooks: Array<{
    status: Status
    callback: (
      response?:
        | NonNullable<PushEventMap[E]['error_payload']>
        | NonNullable<PushEventMap[E]['success_payload']>,
    ) => void
  }> = []

  /** The ref of the push. */
  private _ref: string | undefined

  /** The internal reply event name. */
  private refEvent: string | undefined

  /** The push timeout in milliseconds. */
  private _timeout: number

  /**
   * @constructor
   * @param channel The Channel that the Push belongs to
   * @param event The event name, for example `"phx_join"`
   * @param payload The payload for the message, for example `{user_id: 123}`
   * @param timeout The push timeout in milliseconds
   */
  constructor(
    // We do not care about the ServerEventMap of channel here.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly channel: Channel<PushEventMap, any>,
    private readonly event: E,
    private readonly payload: PushEventMap[E]['request_payload'],
    timeout: number,
  ) {
    this._timeout = timeout
  }

  /** @returns The push timeout in milliseconds. */
  get timeout() {
    return this._timeout
  }

  /** @returns The push event ref. */
  get ref() {
    return this._ref
  }

  /** @returns The push event name. */
  get name() {
    return this.event
  }

  /** Resends the push. */
  resend() {
    this.reset()
    this.send()
  }

  /** Sends the push. */
  send() {
    if (this.hasReceived('timeout')) {
      return
    }
    this.startTimeout()
    if (!this._ref) {
      throw new Error('Push event must have a ref')
    }
    this.channel.socket.push({
      topic: this.channel.topic,
      event: this.event,
      payload: this.payload,
      ref: this._ref,
      join_ref: this.channel.joinRef(),
    })
  }

  /**
   * @param status Reply event status code
   * @param callback
   */
  receive(
    status: 'ok',
    callback: (
      response: NonNullable<PushEventMap[E]['success_payload']>,
    ) => void,
  ): this

  receive(
    status: 'error',
    callback: (response: NonNullable<PushEventMap[E]['error_payload']>) => void,
  ): this

  receive(status: 'timeout', callback: () => void): this

  receive(status: Status, callback: (response: any) => void) {
    if (this.hasReceived(status)) {
      callback(this.receivedPayload?.response)
    }

    this.recHooks.push({ status, callback })
    return this
  }

  /** Resets the push. */
  reset() {
    this.cancelRefEvent()
    this._ref = undefined
    this.refEvent = undefined
    this.receivedPayload = undefined
  }

  /**
   * Triggers the callbacks matching the reply event status code.
   */
  private matchReceive({
    status,
    response,
  }: {
    status: Status
    response:
      | NonNullable<PushEventMap[E]['success_payload']>
      | NonNullable<PushEventMap[E]['error_payload']>
      | undefined
  }) {
    this.recHooks
      .filter((h) => h.status === status)
      .forEach((h) => h.callback(response))
  }

  /** Cancels the internal reply event. */
  private cancelRefEvent() {
    if (!this.refEvent) {
      return
    }
    this.channel.off(this.refEvent)
  }

  /** Cancels the push timeout. */
  cancelTimeout() {
    clearTimeout(this.timeoutTimer)
    this.timeoutTimer = undefined
  }

  /** Starts the push timeout. */
  startTimeout() {
    if (this.timeoutTimer) {
      this.cancelTimeout()
    }
    this._ref = this.channel.socket.makeRef()
    this.refEvent = this.channel.replyEventName(this.ref)

    this.channel.on(this.refEvent, (payload) => {
      this.cancelRefEvent()
      this.cancelTimeout()
      this.receivedPayload = payload
      this.matchReceive(payload)
    })

    this.timeoutTimer = setTimeout(() => {
      this.trigger('timeout', {})
    }, this._timeout)
  }

  /**
   * @returns Whether the push has received a reply with the given status.
   */
  private hasReceived(status: Status) {
    return this.receivedPayload && this.receivedPayload.status === status
  }

  /** Triggers the internal reply event. */
  trigger(status: Status, response) {
    if (this.refEvent) {
      this.channel.trigger(this.refEvent, { status, response })
    }
  }
}
